import { forwardRef } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Button, { type ButtonProps } from '@mui/material/Button';
import Chip, { type ChipProps } from '@mui/material/Chip';
import Tab, { type TabProps } from '@mui/material/Tab';
import InputLabel, { type InputLabelProps } from '@mui/material/InputLabel';
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import FormControlLabel, { type FormControlLabelProps } from '@mui/material/FormControlLabel';
import ListItemText, { type ListItemTextProps } from '@mui/material/ListItemText';
import MenuItem, { type MenuItemProps } from '@mui/material/MenuItem';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

type TranslateFn = (key: string, fallback?: string) => string;

function tx(value: unknown, t: TranslateFn): unknown {
  return typeof value === 'string' ? translateUiText(value, t) : value;
}

/** TextField that auto-translates string label / placeholder / helperText. */
export const TTextField = forwardRef<HTMLDivElement, TextFieldProps>(function TTextField(
  { label, placeholder, helperText, ...props },
  ref,
) {
  const { t } = useI18n();
  return (
    <TextField
      ref={ref}
      {...props}
      label={tx(label, t) as TextFieldProps['label']}
      placeholder={typeof placeholder === 'string' ? translateUiText(placeholder, t) : placeholder}
      helperText={tx(helperText, t) as TextFieldProps['helperText']}
    />
  );
});

/** Button that auto-translates string children. */
export const TButton = forwardRef<HTMLButtonElement, ButtonProps>(function TButton(
  { children, 'aria-label': ariaLabel, ...props },
  ref,
) {
  const { t } = useI18n();
  return (
    <Button
      ref={ref}
      {...props}
      aria-label={typeof ariaLabel === 'string' ? translateUiText(ariaLabel, t) : ariaLabel}
    >
      {typeof children === 'string' ? translateUiText(children, t) : children}
    </Button>
  );
});

export const TChip = forwardRef<HTMLDivElement, ChipProps>(function TChip(
  { label, ...props },
  ref,
) {
  const { t } = useI18n();
  return <Chip ref={ref} {...props} label={tx(label, t) as ChipProps['label']} />;
});

export function TTab({ label, ...props }: TabProps) {
  const { t } = useI18n();
  return <Tab {...props} label={tx(label, t) as TabProps['label']} />;
}

export function TInputLabel({ children, ...props }: InputLabelProps) {
  const { t } = useI18n();
  return (
    <InputLabel {...props}>
      {typeof children === 'string' ? translateUiText(children, t) : children}
    </InputLabel>
  );
}

export const TTooltip = forwardRef<HTMLDivElement, TooltipProps>(function TTooltip(
  { title, ...props },
  ref,
) {
  const { t } = useI18n();
  return <Tooltip ref={ref} {...props} title={tx(title, t) as TooltipProps['title']} />;
});

export function TFormControlLabel({ label, ...props }: FormControlLabelProps) {
  const { t } = useI18n();
  return <FormControlLabel {...props} label={tx(label, t) as FormControlLabelProps['label']} />;
}

export function TListItemText({ primary, secondary, children, ...props }: ListItemTextProps) {
  const { t } = useI18n();
  return (
    <ListItemText
      {...props}
      primary={tx(primary, t) as ListItemTextProps['primary']}
      secondary={tx(secondary, t) as ListItemTextProps['secondary']}
    >
      {typeof children === 'string' ? translateUiText(children, t) : children}
    </ListItemText>
  );
}

export const TMenuItem = forwardRef<HTMLLIElement, MenuItemProps>(function TMenuItem(
  { children, ...props },
  ref,
) {
  const { t } = useI18n();
  return (
    <MenuItem ref={ref} {...props}>
      {typeof children === 'string' ? translateUiText(children, t) : children}
    </MenuItem>
  );
});
